/**
 * Trees copy/render hooks.
 *
 * The port intentionally uses the engine's XY horizontal / Z-up convention.
 * `1 - smoothstep(0.8 * radius, radius, distance)` is the deterministic
 * equivalent of PlayCanvas' reversed-edge expression.
 */
export const GSPLAT_TREES_COPY_GLSL = /* glsl */ `
uniform highp sampler2D uSphereTex;
uniform highp sampler2D uGridTex;
uniform highp sampler2D uListTex;
uniform vec2 uGridMin;
uniform vec2 uGridInvCell;
uniform vec2 uGridDims;

void modifySplatCenter(inout vec3 center) {}
void modifySplatRotationScale(vec3 originalCenter, vec3 modifiedCenter, inout vec4 rotation, inout vec3 scale) {}

#ifndef GSPLAT_HAS_TREE_BIND
void writeTreeBind(vec4 value) {}
#endif

void modifySplatColor(vec3 center, inout vec4 color) {
    float bestSway = 0.0;
    float bestHeight = 0.0;
    float bestIndex = 0.0;
    ivec2 cell = ivec2(floor((center.xy - uGridMin) * uGridInvCell));
    if (cell.x >= 0 && cell.y >= 0 && float(cell.x) < uGridDims.x && float(cell.y) < uGridDims.y) {
        vec4 grid = texelFetch(uGridTex, cell, 0);
        int start = int(grid.r + 0.5);
        int count = int(grid.g + 0.5);
        for (int k = 0; k < 256; k++) {
            if (k >= count) break;
            int sphereIndex = int(texelFetch(uListTex, ivec2(start + k, 0), 0).r + 0.5);
            vec4 sphere = texelFetch(uSphereTex, ivec2(sphereIndex, 0), 0);
            float radius = sphere.w;
            float distanceToSphere = length(center - sphere.xyz);
            if (distanceToSphere < radius) {
                float bottomZ = sphere.z - radius;
                float height = clamp((center.z - bottomZ) / (2.0 * radius), 0.0, 1.0);
                float edgeFade = 1.0 - smoothstep(radius * 0.8, radius, distanceToSphere);
                float sway = pow(height, 1.3) * edgeFade;
                if (sway > bestSway) {
                    bestSway = sway;
                    bestHeight = height;
                    bestIndex = float(sphereIndex + 1);
                }
            }
        }
    }
    float low = mod(bestIndex, 256.0) / 255.0;
    float high = floor(bestIndex / 256.0) / 255.0;
    writeTreeBind(vec4(bestSway, bestHeight, low, high));
}
`

export const GSPLAT_TREES_COPY_WGSL = /* wgsl */ `
fn modifySplatCenter(center: ptr<function, vec3f>) {}
fn modifySplatRotationScale(originalCenter: vec3f, modifiedCenter: vec3f, rotation: ptr<function, vec4f>, scale: ptr<function, vec3f>) {}

#ifndef GSPLAT_HAS_TREE_BIND
fn writeTreeBind(value: vec4f) {}
#endif

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
    var bestSway = 0.0;
    var bestHeight = 0.0;
    var bestIndex = 0.0;
    let cell = vec2i(floor((center.xy - uniform.uGridMin) * uniform.uGridInvCell));
    if (cell.x >= 0 && cell.y >= 0 && f32(cell.x) < uniform.uGridDims.x && f32(cell.y) < uniform.uGridDims.y) {
        let grid = textureLoad(uGridTex, cell, 0);
        let start = i32(grid.r + 0.5);
        let count = i32(grid.g + 0.5);
        for (var k = 0; k < 256; k++) {
            if (k >= count) { break; }
            let sphereIndex = i32(textureLoad(uListTex, vec2i(start + k, 0), 0).r + 0.5);
            let sphere = textureLoad(uSphereTex, vec2i(sphereIndex, 0), 0);
            let radius = sphere.w;
            let distanceToSphere = distance(center, sphere.xyz);
            if (distanceToSphere < radius) {
                let bottomZ = sphere.z - radius;
                let height = clamp((center.z - bottomZ) / (2.0 * radius), 0.0, 1.0);
                let edgeFade = 1.0 - smoothstep(radius * 0.8, radius, distanceToSphere);
                let sway = pow(height, 1.3) * edgeFade;
                if (sway > bestSway) {
                    bestSway = sway;
                    bestHeight = height;
                    bestIndex = f32(sphereIndex + 1);
                }
            }
        }
    }
    let low = (bestIndex % 256.0) / 255.0;
    let high = floor(bestIndex / 256.0) / 255.0;
    writeTreeBind(vec4f(bestSway, bestHeight, low, high));
}
`

export const GSPLAT_TREES_RENDER_GLSL = /* glsl */ `
uniform float uTime;
uniform vec2 uWindDir;
uniform float uWindStrength;
uniform float uWindSpeed;
uniform float uGustiness;
uniform float uFlutter;
uniform float uWaveTravel;
uniform float uEditSelected;

#ifndef GSPLAT_HAS_TREE_BIND
vec4 loadTreeBind() { return vec4(0.0); }
#endif

void modifySplatCenter(inout vec3 center) {
    vec4 bind = loadTreeBind();
    if (bind.x > 0.0) {
        float treeIndex = floor(bind.z * 255.0 + 0.5) + floor(bind.w * 255.0 + 0.5) * 256.0 - 1.0;
        float phase = fract(treeIndex * 0.61803) * 6.28318;
        float oscillation = sin(uTime * uWindSpeed - bind.y * uWaveTravel + phase);
        float gust = 1.0 + uGustiness * sin(uTime * 0.5 + phase);
        float magnitude = uWindStrength * bind.x * gust * oscillation;
        float flutter = uWindStrength * uFlutter * bind.x * sin(uTime * uWindSpeed * 3.1 + phase * 2.3 + bind.y * 17.0);
        center.x += uWindDir.x * magnitude - uWindDir.y * flutter;
        center.y += uWindDir.y * magnitude + uWindDir.x * flutter;
    }
}

void modifySplatCovariance(vec3 originalCenter, vec3 modifiedCenter, inout vec3 covA, inout vec3 covB) {}
void modifySplatColor(vec3 center, inout vec4 color) {
    if (uEditSelected >= 0.0) {
        vec4 bind = loadTreeBind();
        float treeIndex = floor(bind.z * 255.0 + 0.5) + floor(bind.w * 255.0 + 0.5) * 256.0 - 1.0;
        if (bind.x > 0.0 && abs(treeIndex - uEditSelected) < 0.5) {
            color.rgb = mix(color.rgb, vec3(1.0, 0.15, 0.1), 0.6);
        }
    }
}
`

export const GSPLAT_TREES_RENDER_WGSL = /* wgsl */ `
#ifndef GSPLAT_HAS_TREE_BIND
fn loadTreeBind() -> vec4f { return vec4f(0.0); }
#endif

fn modifySplatCenter(center: ptr<function, vec3f>) {
    let bind = loadTreeBind();
    if (bind.x > 0.0) {
        let treeIndex = floor(bind.z * 255.0 + 0.5) + floor(bind.w * 255.0 + 0.5) * 256.0 - 1.0;
        let phase = fract(treeIndex * 0.61803) * 6.28318;
        let oscillation = sin(uniform.uTime * uniform.uWindSpeed - bind.y * uniform.uWaveTravel + phase);
        let gust = 1.0 + uniform.uGustiness * sin(uniform.uTime * 0.5 + phase);
        let magnitude = uniform.uWindStrength * bind.x * gust * oscillation;
        let flutter = uniform.uWindStrength * uniform.uFlutter * bind.x * sin(uniform.uTime * uniform.uWindSpeed * 3.1 + phase * 2.3 + bind.y * 17.0);
        (*center) = vec3f(
            (*center).x + uniform.uWindDir.x * magnitude - uniform.uWindDir.y * flutter,
            (*center).y + uniform.uWindDir.y * magnitude + uniform.uWindDir.x * flutter,
            (*center).z
        );
    }
}

fn modifySplatRotationScale(originalCenter: vec3f, modifiedCenter: vec3f, rotation: ptr<function, vec4f>, scale: ptr<function, vec3f>) {}
fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
    if (uniform.uEditSelected >= 0.0) {
        let bind = loadTreeBind();
        let treeIndex = floor(bind.z * 255.0 + 0.5) + floor(bind.w * 255.0 + 0.5) * 256.0 - 1.0;
        if (bind.x > 0.0 && abs(treeIndex - uniform.uEditSelected) < 0.5) {
            (*color) = vec4f(mix((*color).rgb, vec3f(1.0, 0.15, 0.1), 0.6), (*color).a);
        }
    }
}
`
